import { type FC, useEffect, useLayoutEffect, useRef, useState } from "react";

import type { VideoState } from "../../../shared/types/worksheet";

import { fetchVideoCaption } from "api/api-video-captions";
import {
  VideoChangeMessage,
  WindowMessages,
  addProjectorMessageListener,
  removeProjectorMessageListener,
  sendMessageToProjectorView,
} from "components/materials/presentation/projector/projector-messaging";
import { toastLocalizedOperationError } from "utils/alerts";
import { rollbarAndLogError } from "utils/logger";
import { usePresentation } from "utils/presentation";

type VideoAssetProps = {
  id: string;
  url: string;
  className?: string;
  shouldSyncWithInstructorView?: boolean;
  blockHeight?: number;
  videoState?: VideoState;
  setVideoState?: (state: Partial<VideoState>) => void;
  tracks?: { src: string; srcLang: string; label: string; kind?: string }[];
};

const VideoCaptionTrack: FC<NonNullable<VideoAssetProps["tracks"]>[number]> = ({
  src,
  srcLang,
  label,
  kind,
}) => {
  const [blobUrl, setBlobUrl] = useState<string>();
  const blobUrlRef = useRef<string | undefined>(undefined);
  const trackRef = useRef<HTMLTrackElement | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let createdBlobUrl: string | undefined;
    fetchVideoCaption(src, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        // The effect cleanup and track error handler both revoke this URL.
        // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke
        createdBlobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = createdBlobUrl;
        setBlobUrl(createdBlobUrl);
      })
      .catch(() => {
        // The API helper reports failures; unavailable captions must not interrupt video playback.
      });
    return () => {
      controller.abort();
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
      if (blobUrlRef.current === createdBlobUrl) blobUrlRef.current = undefined;
    };
  }, [src]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handleTrackError = () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = undefined;
      setBlobUrl(undefined);
      rollbarAndLogError("Unable to load video captions (invalid WebVTT)");
      toastLocalizedOperationError("invalid_video_caption");
    };
    track.addEventListener("error", handleTrackError);
    return () => track.removeEventListener("error", handleTrackError);
  }, [blobUrl]);

  return blobUrl ? (
    <track kind={kind} label={label} ref={trackRef} src={blobUrl} srcLang={srcLang} />
  ) : null;
};

const VideoAsset: FC<VideoAssetProps> = ({
  id,
  url,
  className,
  shouldSyncWithInstructorView = false,
  blockHeight,
  videoState,
  setVideoState,
  tracks,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const { isPresenting, hasProjectorView, isFollowing } = usePresentation();
  const userIsProjecting = isPresenting && hasProjectorView;
  const shouldReadVideoStateFromBlock = !isPresenting && isFollowing;
  const muteAudio = userIsProjecting && !shouldSyncWithInstructorView;
  const [videoHeightExceedsBlockHeight, setVideoHeightExceedsBlockHeight] = useState(false);

  useLayoutEffect(() => {
    if (videoRef.current) {
      const videoElement = videoRef.current;

      const handleLoadedMetadata = () => {
        const videoHeight = videoElement.clientHeight;
        if (blockHeight && videoHeight > blockHeight) {
          setVideoHeightExceedsBlockHeight(true);
        }
      };

      if (videoElement.readyState >= 1) {
        handleLoadedMetadata();
      } else {
        videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      }

      return () => {
        videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [blockHeight]);

  // Listen to video events and send messages to the projector or save
  // the state change to the doc if we're presenting.
  useEffect(() => {
    // If we are receiving messages from the instructor view,
    // we don't want to send messages
    if (shouldSyncWithInstructorView) {
      return;
    }

    const videoComponent = videoRef.current;

    const sendPlayChange = () => {
      const newState = {
        playing: !videoRef.current?.paused,
        currentTimeInSeconds: videoRef.current?.currentTime || 0,
        playbackRate: videoRef.current?.playbackRate || 1,
      };

      if (userIsProjecting) {
        const message: VideoChangeMessage = {
          type: WindowMessages.VIDEO_CHANGE,
          id,
          ...newState,
        };

        sendMessageToProjectorView(message);
      }

      if (setVideoState) {
        setVideoState(newState);
      }
    };

    const sendTimeChange = () => {
      if (userIsProjecting) {
        const message: VideoChangeMessage = {
          type: WindowMessages.VIDEO_CHANGE,
          id,
          currentTimeInSeconds: videoRef.current?.currentTime,
        };

        sendMessageToProjectorView(message);
      }

      if (setVideoState) {
        setVideoState({
          currentTimeInSeconds: videoRef.current?.currentTime,
        });
      }
    };

    const sendRateChange = () => {
      if (userIsProjecting) {
        const message: VideoChangeMessage = {
          type: WindowMessages.VIDEO_CHANGE,
          id,
          playbackRate: videoRef.current?.playbackRate,
        };

        sendMessageToProjectorView(message);
      }

      if (setVideoState) {
        setVideoState({
          playbackRate: videoRef.current?.playbackRate,
        });
      }
    };

    if ((userIsProjecting || isPresenting) && videoComponent) {
      videoComponent.addEventListener("play", sendPlayChange);
      videoComponent.addEventListener("pause", sendPlayChange);
      videoComponent.addEventListener("seeked", sendTimeChange);
      videoComponent.addEventListener("ratechange", sendRateChange);
    }

    return () => {
      videoComponent?.removeEventListener("play", sendPlayChange);
      videoComponent?.removeEventListener("pause", sendPlayChange);
      videoComponent?.removeEventListener("seeked", sendTimeChange);
      videoComponent?.removeEventListener("ratechange", sendRateChange);
    };
  }, [userIsProjecting, isPresenting, shouldSyncWithInstructorView, id, setVideoState]);

  // Here the projector view listens for events of the video state changing
  // TODO: consider just grabbing this from the doc if the lag isn't bad
  useEffect(() => {
    const handleVideoChangeMessage = (message: MessageEvent<VideoChangeMessage>) => {
      if (
        message.data.type === WindowMessages.VIDEO_CHANGE &&
        videoRef.current &&
        shouldSyncWithInstructorView &&
        message.data.id === id
      ) {
        if (message.data.currentTimeInSeconds) {
          videoRef.current.currentTime = message.data.currentTimeInSeconds;
        }

        if (message.data.playbackRate) {
          videoRef.current.playbackRate = message.data.playbackRate;
        }

        if (message.data.playing && videoRef.current.paused) {
          videoRef.current.play();
        } else if (message.data.playing === false && !videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
    };

    if (shouldSyncWithInstructorView) {
      addProjectorMessageListener(handleVideoChangeMessage);
    }

    return () => {
      removeProjectorMessageListener(handleVideoChangeMessage);
    };
  }, [shouldSyncWithInstructorView, id]);

  // If we're following we need to react to video state changes by the presenter.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoState || !shouldReadVideoStateFromBlock) return;

    if (videoState.currentTimeInSeconds) {
      video.currentTime = videoState.currentTimeInSeconds;
    }

    if (videoState.playbackRate) {
      video.playbackRate = videoState.playbackRate;
    }

    if (videoState.playing === true && video.paused) {
      video.play();
    } else if (videoState.playing === false && !video.paused) {
      video.pause();
    }
  }, [videoState, shouldReadVideoStateFromBlock]);

  return (
    <video
      className={className}
      data-vertical-asset={videoHeightExceedsBlockHeight}
      muted={muteAudio}
      ref={videoRef}
      src={url}
      controls
    >
      {tracks &&
        tracks.map((track) => (
          // Keying by source cancels the old download and discards its track on replacement.
          <VideoCaptionTrack
            key={track.src}
            kind={track.kind}
            label={track.label}
            src={track.src}
            srcLang={track.srcLang}
          />
        ))}
    </video>
  );
};

// For Lazy loading it's easier to load a default component
// eslint-disable-next-line import/no-default-export
export default VideoAsset;
